import React, {useEffect, useRef} from 'react';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import {Card} from '../general/card';
import {Text} from "../general/typography";
import Loader from "../../components/general/loader";
import {DashboardProvider, useDashboard} from "../../context/dashboard_provider";
import {Accordion, AccordionDetails, AccordionSummary, Box, Divider, Grid, Stack, Typography} from "@mui/material";
import {useTheme} from "@emotion/react";
import {styled} from "@mui/system";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ExpandLess} from '@mui/icons-material';
import { useState } from "react";
import { GridChart } from "./grid_chart";
import { InternalChart } from "./internal_chart";
import { FlexChart } from "./flex_chart";
import { ProductionChart } from "./production_chart";
import { SOCChart } from "./soc_chart";
import { PeakChart } from "./peak_chart";
import { ImbalancePriceEpexChart } from "./imbalance_price_epex_chart";
import { ForecastChart } from "./forecast_chart";
import { LynxChart } from "./lynx_chart";
import { ChartCard } from './chart_card';

ChartJS.register(
    TimeScale,
    LinearScale,
    CategoryScale,
    LineController,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Filler,
    Legend
);

export const getBarTimeChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        mode: 'index',
        intersect: false,
    },
    plugins: {
        legend: {
            position: 'bottom',
            display: true,
        },
    },
    scales: {
        x: {
            type: 'time',
            time: {
                tooltipFormat: 'dd/MM/yyyy HH:mm',
                displayFormats: {
                    hour: 'HH:mm'
                },
                unit: "hour",
                stepSize: 1,
            },
            stacked: true,
            grid: {
                offset: false,
            }
        },
        y: {
            stacked: true,
        },

    },
})


export const getLiveChartOptions = () => ({
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        mode: 'x',
        intersect: false,
    },
    plugins: {
        legend: {
            position: 'bottom',
            display: true,
        },
    },
    scales: {
        y: {},

    },
})

export function BarTimeChartSynced({data, options}) {
    const {tooltipXPos} = useDashboard();
    const lastTooltip = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
        const chart = chartRef.current;
        const handleHover = (e) => {
            const point = chart.getElementsAtEventForMode(e, "nearest", {intersect: false}, true);
            if (point[0]) {
                tooltipXPos.current = point[0].index;
            }
        };

        const handleLeave = (e) => {
            tooltipXPos.current = null;
        }

        chart.canvas.onmousemove = handleHover;
        chart.canvas.onmouseleave = handleLeave;

        const interval = setInterval(() => {
            if (lastTooltip.current === tooltipXPos.current) {
                return;
            }

            const chart = chartRef.current;
            if (tooltipXPos.current && tooltipXPos.current !== null) {
                // const index = chart.data.labels.findIndex(label => label === tooltipXPos);
                const index = tooltipXPos.current
                if (index !== null && index >= 0) {
                    chart.tooltip.setActiveElements(chart.config.data.datasets.map((obj, dataSetIndex) => ({
                        datasetIndex: dataSetIndex,
                        index: index
                    })));
                }
            } else {
                chart.tooltip.setActiveElements([])
            }

            lastTooltip.current = tooltipXPos.current
            chart.update()
        }, 100)

        return () => clearInterval(interval)
    }, []);

    const o = options ? options : getBarTimeChartOptions()
    o.events = ['click']

    return <Bar ref={chartRef} data={data} width={"100%"} options={o}/>
};

export function BarTimeChart({data, options}) {
    return <Bar data={data} width={"100%"} options={options ? options : getBarTimeChartOptions()}/>
};

export function ChartDivider({data, Chart, title, isLoading}) {
    const cardSx = {height: 370, pb: 7, border: "0px"}
    const NoData = <Text sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>No data
        available;</Text>
    return <Card sx={cardSx} title={title}>
        {isLoading ? <Loader/> : data ? Chart : NoData}
        {isLoading ? <Loader/> : data ? <Divider/> : null}
    </Card>
}


function TitleCard({title, backgroundColor, open, textColor = null, toUpper = true}) {
    const cardSx = {
        width: '100%',
        padding: 0,
        color: backgroundColor != null ? backgroundColor
            : (theme) => theme.palette.background.border,
        border:  backgroundColor == null ? 1 : 0,
    }
    return (
        <Card sx={cardSx} headerMargin={2}>
            <Box sx={{
                pl: 2,
                pb: 2,
                pt: 0,
                display: 'flex',
                alignItems: 'center',
                color: textColor != null ? textColor : ((theme) => theme.palette.text.primary)
            }}>
                <Stack alignItems="center" direction="row" gap={1}>
                    {open ? <ExpandLess/> : <ExpandMoreIcon/>}
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                        {title.toUpperCase()}
                    </Typography>
                </Stack>

            </Box>
        </Card>
    );
}

const StyledAccordion = styled(Accordion)({
    width: '100%',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    border: 'none',
    elevation: 0,
    margingTop: '0px',
});

const StyledAccordionSummary = styled(AccordionSummary)({
    width: '100%',
    padding: 0,
    border: 'none',
});

const StyledAccordionDetails = styled(AccordionDetails)({
    width: '100%',
    padding: 0,
    border: 'none',
});


function MeteringCollapsableCard({titleCard, children, onChange}) {
    return (
        <StyledAccordion disableGutters sx={{'&:before': {height: '0px'}, m: 0, p: 0}}
                         defaultExpanded={true}
                         onChange={(event, expanded) => onChange()}
        >
            <StyledAccordionSummary>
                {titleCard}
            </StyledAccordionSummary>
            <StyledAccordionDetails>
                <Grid container spacing={2}>
                    {children}
                </Grid>
            </StyledAccordionDetails>
        </StyledAccordion>
    );
}

export default function Metering({
  metering,
  profits,
  actuals,
  forecast,
  prices,
  fetchingMetering,
  fetchingActuals,
  fetchingForecast,
  gridReferenceLines,
  fetchingPrices,
  fetchingPeak,
  Optimizer,
  peakData,
  multiDay = false,
}) {
  const [openMarketPrices, setOpenMarketPrices] = useState(true);
  const [openMetering, setOpenMetering] = useState(true);
  const [openForecast, setOpenForecast] = useState(true);

  const theme = useTheme();

    return <DashboardProvider>
      <MeteringCollapsableCard
        onChange={() => setOpenMarketPrices(!openMarketPrices)}
        titleCard={<TitleCard title="Market Prices" textColor={theme.palette.primary.main} open={openMarketPrices} />}
      >
        <Grid item sm={12} md={6}>
          <ChartCard
            Chart={<ImbalancePriceEpexChart prices={prices} multiDay={multiDay} />}
            title="Imbalance and Epex Prices"
            data={prices}
            isLoading={fetchingPrices}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ChartCard
            Chart={<ForecastChart metering={forecast} multiDay={multiDay} />}
            title="Forecast & Actuals"
            data={forecast}
            isLoading={fetchingActuals || fetchingForecast}
            noDataMessage={Optimizer === "EPEX" ? "Individual forecast is not applicable for EPEX steering" : undefined}
          />
        </Grid>

      </MeteringCollapsableCard>
        <MeteringCollapsableCard onChange={() => setOpenMetering(!openMetering)}
                                 titleCard={<TitleCard title='Device Metering' textColor={theme.palette.primary.main} open={openMetering}/>}>
            <Grid item xs={12} sm={6}>
                <ChartCard Chart={<GridChart metering={metering} gridReferenceLines={gridReferenceLines}/>} title="Grid" data={metering}
                           isLoading={fetchingMetering}/>
            </Grid>
            <Grid item xs={12} sm={6}>
                <ChartCard Chart={<InternalChart metering={metering}/>} title="Internal" data={metering}
                           isLoading={fetchingMetering}/>
            </Grid>
            <Grid item xs={12} sm={6}>
                <ChartCard Chart={<FlexChart metering={metering}/>} title="Flex" data={metering}
                           isLoading={fetchingMetering}/>
            </Grid>
            <Grid item xs={12} sm={6}>
                <ChartCard Chart={<ProductionChart metering={metering}/>} title="Production" data={metering}
                           isLoading={fetchingMetering}/>
            </Grid>
            <Grid item xs={12} sm={6}>
                <ChartCard
                    Chart={<SOCChart metering={metering} multiDay={multiDay} />}
                    title="SOC"
                    data={metering}
                    isLoading={fetchingMetering}
                />
            </Grid>
                <Grid item sm={12} md={6}>
                <ChartCard
                    Chart={<PeakChart peakData={peakData} multiDay={multiDay} />}
                    title="Peak"
                    data={peakData}
                    isLoading={fetchingPeak}
                />
                </Grid>
        </MeteringCollapsableCard>
        <MeteringCollapsableCard onChange={() => setOpenForecast(!openForecast)}
                                 titleCard={<TitleCard title='Forecast & Network Operator' textColor={theme.palette.primary.main} open={openForecast}/>}>
            <Grid item xs={12} sm={6}>
                <ChartCard Chart={<LynxChart metering={actuals}/>} title="Network operator metering" data={actuals}
                           isLoading={fetchingActuals}/>
            </Grid>
        </MeteringCollapsableCard>
    </ DashboardProvider>
}
